/**
 * The class diagram's layout: where each box, package and arrow goes. Pure: the same graph,
 * filters and arrangement give the same picture on every page, so pages that share a board
 * agree without telling each other anything but the boxes moved by hand.
 *
 * Like objects on a Miro board, **where a box is never depends on the filters**: positions are
 * worked out from the whole graph (every node, every link, groups as packages), and the
 * filters (categories, link kinds, the packages and bundles switches) only decide what is
 * drawn. Hiding a category leaves a gap; showing it again puts the boxes back in it.
 *
 * - **Automatic** (nobody moved a box): layers, base types on top. A link reads "from uses /
 *   extends / implements / depends on to", so `to` goes above `from`. Inside a group, its
 *   nodes and sub-groups are laid out the same way, and the group becomes one block (a UML
 *   package) in its parent's layers. Layers too wide for a tidy picture wrap into more rows.
 * - **Arranged** (someone moved a box, kept in the graph as `node.diagram`): boxes stay where
 *   they were put; a box nobody placed
 *   yet (a new node) goes in the first free spot below a box it is linked to, else below
 *   everything. Packages always wrap their boxes.
 * - **Arrows** go straight from border to border; two links between the same boxes are drawn
 *   side by side. With bundles on, links from one package to another are also gathered into
 *   bundles (`bundles.ts`), drawn as one arrow until the user looks at them.
 * - **Folded** packages (`group.collapsed`, saved with the graph) are drawn closed: a small box
 *   at their frame's corner saying how many boxes they hold, which are hidden, their arrows
 *   drawn to the closed package. Like a filter, folding moves nothing.
 */
import type { Graph } from "@protocol/Graph";
import type { Link } from "@protocol/Link";
import type { Node } from "@protocol/Node";
import { shownWith } from "../../shared/categories";
import { linkKindShown } from "../../shared/kinds";
import type { BoxShape } from "./boxes";
import { border, diagramBundles, type DiagramBundle } from "./bundles";
import { foldsOf } from "../scene/folds";
import { COLUMN_GAP, FOLDED_BODY, FOLDED_WIDTH, PACKAGE_PAD, PACKAGE_TAB, ROW_GAP } from "./constants";

/** Box id → `[x, y]` of its top-left corner. */
export type BoxPositions = Record<string, [number, number]>;

/** What decides what is drawn (never where: see the module documentation). */
export interface DiagramFilters {
  /** The categories hidden, lower case (`""`: nodes in no category). */
  hiddenCategories: readonly string[];
  /** Which link kinds are shown. */
  linkKinds: Record<string, boolean>;
  /** Whether groups are drawn as packages. */
  groups: boolean;
  /** Whether links between packages are bundled (only with packages shown). */
  bundles: boolean;
}

/** A box on the diagram. */
export interface DiagramBox extends BoxShape {
  /** The node's id. */
  id: string;
  /** The node as the core describes it. */
  node: Node;
  /** Top-left corner, world units. */
  x: number;
  /** Top-left corner, world units. */
  y: number;
}

/** A group drawn as a UML package: a frame with a name tab. */
export interface DiagramPackage {
  /** The group's id. */
  id: string;
  /** The group's name, written in the tab. */
  name: string;
  /** Top-left corner of the tab (the frame starts {@link PACKAGE_TAB} lower). */
  x: number;
  /** Top-left corner of the tab. */
  y: number;
  /** Width of the frame. */
  w: number;
  /** Height of the tab and the frame together. */
  h: number;
  /** How deep it is (0: in no other package). Draw the shallow ones first. */
  depth: number;
  /** The package it is in. */
  parent: string | null;
  /** Set when the group is folded: drawn closed, holding this many boxes (hidden). */
  folded?: number;
}

/** An arrow from one box to another. */
export interface DiagramEdge {
  /** The link as the core describes it. */
  link: Link;
  /** Where it starts (on `from`'s border). */
  x1: number;
  /** Where it starts. */
  y1: number;
  /** Where it ends (on `to`'s border, the arrowhead). */
  x2: number;
  /** Where it ends. */
  y2: number;
}

/** The whole diagram. */
export interface DiagramLayout {
  /** Where every node's box is (top-left corner), shown or hidden by the filters. */
  positions: BoxPositions;
  /** Every node inside each package, deep, shown or hidden (what dragging the package moves). */
  contents: Map<string, string[]>;
  /** The boxes shown. */
  boxes: DiagramBox[];
  /** Boxes by node id. */
  byId: Map<string, DiagramBox>;
  /** The packages shown (groups with something shown inside), shallow ones first. */
  packages: DiagramPackage[];
  /** Packages by group id. */
  packageById: Map<string, DiagramPackage>;
  /** The arrows shown (bundled ones included). */
  edges: DiagramEdge[];
  /** Links between packages gathered into one arrow (none when bundling is off). */
  bundles: DiagramBundle[];
  /** Bundles by key (the same keys as the nodal view's). */
  bundleByKey: Map<string, DiagramBundle>;
  /** The bundle each bundled arrow is in. */
  bundleOf: Map<DiagramEdge, DiagramBundle>;
  /** What everything covers (`null`: nothing shown). */
  bounds: { x: number; y: number; w: number; h: number } | null;
}

/** What is laid out in one container (the diagram or a package): boxes and sub-packages. */
type Item = { kind: "node"; id: string } | { kind: "group"; id: string };

/** An item placed inside its container, relative to the container's content corner. */
interface Placed {
  item: Item;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The key of an item, unique among nodes and groups. */
const keyOf = (item: Item) => `${item.kind === "node" ? "n" : "g"}:${item.id}`;

/**
 * Lays out the diagram of `graph`, showing what these filters let through. Boxes put by hand
 * are where their node says (`node.diagram`), or where `moving` says while a drag is under way
 * (it wins); the others where the automatic layout of the whole graph puts them.
 */
export function layoutDiagram(graph: Graph, filters: DiagramFilters, moving: BoxPositions, shapeOf: (node: Node) => BoxShape): DiagramLayout {
  // Positions come from everything; the filters only pick what is drawn, at the end.
  const nodes = graph.nodes;
  const known = new Set(nodes.map((n) => n.id));
  const allLinks = graph.links.filter((l) => known.has(l.from) && known.has(l.to) && l.from !== l.to);
  const shown = new Set(nodes.filter((n) => shownWith(n.categories, filters.hiddenCategories)).map((n) => n.id));
  const links = allLinks.filter((l) => linkKindShown(filters.linkKinds, l.kind) && shown.has(l.from) && shown.has(l.to));
  const shapes = new Map(nodes.map((n) => [n.id, shapeOf(n)]));

  // The group tree, keeping only groups with a node inside.
  const groupById = new Map(graph.groups.map((g) => [g.id, g]));
  /** The group a node is in (`null`: none). */
  const groupOfNode = (n: Node) => (n.group && groupById.has(n.group) ? n.group : null);
  /** The group a group is drawn in. */
  const parentOf = (id: string): string | null => {
    const parent = groupById.get(id)?.parent ?? null;
    return parent && groupById.has(parent) && parent !== id ? parent : null;
  };
  const used = new Set<string>();
  for (const n of nodes) {
    // Walks up, guarding against a loop the core would have refused anyway.
    for (let g = groupOfNode(n), steps = 0; g && !used.has(g) && steps < 1000; g = parentOf(g), steps++) used.add(g);
  }
  /** Children of a container (`null`: the diagram itself), in the graph's order. */
  const children = new Map<string | null, Item[]>();
  const add = (container: string | null, item: Item) => (children.get(container) ?? children.set(container, []).get(container)!).push(item);
  for (const g of graph.groups) if (used.has(g.id)) add(parentOf(g.id), { kind: "group", id: g.id });
  for (const n of nodes) add(groupOfNode(n), { kind: "node", id: n.id });

  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  /** The chain of containers of a node, from the diagram down to its own group. */
  const chainOf = (node: Node): (string | null)[] => {
    const chain: (string | null)[] = [];
    for (let g = groupOfNode(node), steps = 0; g && steps < 1000; g = parentOf(g), steps++) chain.unshift(g);
    return [null, ...chain];
  };
  const chains = new Map(nodes.map((n) => [n.id, chainOf(n)]));
  /** The item right below `chain[at]` holding the node: the sub-group it is in, or itself. */
  const below = (chain: (string | null)[], at: number, id: string): Item => {
    const next = chain[at + 1];
    return next === undefined ? { kind: "node", id } : { kind: "group", id: next! };
  };
  // Each link ties two items of the container where its ends' chains part, and nothing else.
  const tied = new Map<string | null, [string, string][]>();
  for (const l of allLinks) {
    const a = chains.get(l.from)!;
    const b = chains.get(l.to)!;
    let at = 0;
    while (at + 1 < a.length && at + 1 < b.length && a[at + 1] === b[at + 1]) at++;
    const container = a[at]!;
    (tied.get(container) ?? tied.set(container, []).get(container)!).push([keyOf(below(a, at, l.from)), keyOf(below(b, at, l.to))]);
  }

  // Automatic positions, relative to each container's content corner; then made absolute.
  const blocks = new Map<string, { w: number; h: number; inside: Placed[] }>();
  /** Lays out a container's content; its size. */
  const layOut = (container: string | null): { w: number; h: number; inside: Placed[] } => {
    const items = children.get(container) ?? [];
    const size = (item: Item) => {
      if (item.kind === "node") return shapes.get(item.id)!;
      const block = layOut(item.id);
      blocks.set(item.id, block);
      return { w: block.w + 2 * PACKAGE_PAD, h: block.h + 2 * PACKAGE_PAD + PACKAGE_TAB };
    };
    const sized = items.map((item) => ({ item, ...size(item) }));
    return layers(sized, tied.get(container) ?? []);
  };
  const root = layOut(null);

  const boxes: DiagramBox[] = [];
  /** Turns a container's relative positions into absolute ones, from its content corner. */
  const place = (inside: Placed[], ox: number, oy: number) => {
    for (const p of inside) {
      if (p.item.kind === "node") {
        const node = nodeById.get(p.item.id)!;
        boxes.push({ id: node.id, node, ...shapes.get(node.id)!, x: ox + p.x, y: oy + p.y });
      } else place(blocks.get(p.item.id)!.inside, ox + p.x + PACKAGE_PAD, oy + p.y + PACKAGE_TAB + PACKAGE_PAD);
    }
  };
  place(root.inside, -root.w / 2, -root.h / 2);
  // In the graph's order, so later boxes are drawn on top the same way everywhere.
  const order = new Map(graph.nodes.map((n, i) => [n.id, i]));
  boxes.sort((a, b) => order.get(a.id)! - order.get(b.id)!);

  const arranged: BoxPositions = {};
  for (const n of nodes) if (n.diagram) arranged[n.id] = n.diagram;
  Object.assign(arranged, moving);
  if (Object.keys(arranged).length) arrange(boxes, allLinks, arranged);
  const positions: BoxPositions = {};
  for (const b of boxes) positions[b.id] = [b.x, b.y];

  // What is drawn: the boxes the filters show; packages (when shown) around every box of
  // theirs, hidden ones included, so a frame stays put too; those with nothing shown left out.
  const frames = wrapPackages(graph, used, parentOf, groupOfNode, new Map(boxes.map((b) => [b.id, b])));
  const visible = boxes.filter((b) => shown.has(b.id));
  const contents = new Map<string, string[]>();
  for (const b of boxes)
    for (let g = groupOfNode(b.node), steps = 0; g && steps < 1000; g = parentOf(g), steps++) (contents.get(g) ?? contents.set(g, []).get(g)!).push(b.id);
  const showing = new Set<string>();
  for (const b of visible) for (let g = groupOfNode(b.node), steps = 0; g && !showing.has(g) && steps < 1000; g = parentOf(g), steps++) showing.add(g);
  // Folded groups: the outermost one of a chain is drawn closed; everything inside it hides.
  const folds = foldsOf(graph.groups);
  const foldedOf = (b: DiagramBox) => (b.node.group ? folds.get(b.node.group)?.id : undefined);
  const closed = new Map<string, DiagramPackage>();
  for (const f of frames) {
    if (folds.get(f.id)?.id !== f.id) continue;
    const count = (contents.get(f.id) ?? []).filter((id) => shown.has(id)).length;
    closed.set(f.id, { ...f, w: Math.max(FOLDED_WIDTH, f.name.length * 7.5 + 40), h: PACKAGE_TAB + FOLDED_BODY, folded: count });
  }
  const insideFolded = (id: string) => folds.has(id) && folds.get(id)!.id !== id;
  const shownBoxes = visible.filter((b) => !foldedOf(b));
  const packages = [...(filters.groups ? frames.filter((p) => showing.has(p.id) && !closed.has(p.id) && !insideFolded(p.id)) : []), ...closed.values()].sort((a, b) => a.depth - b.depth);
  const byId = new Map(shownBoxes.map((b) => [b.id, b]));
  /** Where an arrow's end is drawn: its box, or the closed package hiding it. */
  const ends = new Map<string, { x: number; y: number; w: number; h: number }>(byId);
  for (const b of visible) {
    const f = foldedOf(b);
    if (f && closed.has(f)) ends.set(b.id, closed.get(f)!);
  }
  const everyBox = new Map(boxes.map((b) => [b.id, b]));
  /** An arrow end's key: the box, or the closed package hiding it. */
  const endKey = (id: string) => (byId.has(id) ? id : `g:${foldedOf(everyBox.get(id)!)}`);
  /** Arrows already drawn between a closed package and another end, per kind: drawn once. */
  const once = new Set<string>();
  const edges = route(
    links.filter((l) => {
      const a = ends.get(l.from);
      const b = ends.get(l.to);
      if (!a || !b || a === b) return false;
      if (byId.has(l.from) && byId.has(l.to)) return true;
      const key = `${endKey(l.from)}\n${endKey(l.to)}\n${l.kind}`;
      if (once.has(key)) return false;
      once.add(key);
      return true;
    }),
    ends,
  );
  const packageById = new Map(packages.map((p) => [p.id, p]));
  const bundles = filters.groups && filters.bundles ? diagramBundles(edges, byId, packageById) : [];
  const bundleOf = new Map(bundles.flatMap((b) => b.links.map((e) => [e, b] as const)));
  return {
    positions,
    contents,
    boxes: shownBoxes,
    byId,
    packages,
    packageById,
    edges,
    bundles,
    bundleByKey: new Map(bundles.map((b) => [b.key, b])),
    bundleOf,
    bounds: boundsOf(shownBoxes, packages),
  };
}

/**
 * Puts items in layers: an item goes one layer below the lowest item it points at, items
 * pointing at nothing on top. Links going round in a circle are cut where the walk finds them.
 * Each layer is then ordered to keep linked items close (a few passes of averages), wrapped
 * when too wide, and centred.
 */
function layers(items: { item: Item; w: number; h: number }[], edges: [string, string][]): { w: number; h: number; inside: Placed[] } {
  if (!items.length) return { w: 0, h: 0, inside: [] };
  const keys = items.map((i) => keyOf(i.item));
  const up = new Map<string, string[]>(keys.map((k) => [k, []]));
  const down = new Map<string, string[]>(keys.map((k) => [k, []]));
  for (const [a, b] of edges) {
    up.get(a)!.push(b);
    down.get(b)!.push(a);
  }

  // Longest path to the top, walking depth first (a link back to the walk is a circle: skipped).
  const layer = new Map<string, number>();
  const walking = new Set<string>();
  const depthOf = (k: string): number => {
    const known = layer.get(k);
    if (known !== undefined) return known;
    walking.add(k);
    let d = 0;
    for (const above of up.get(k)!) if (!walking.has(above)) d = Math.max(d, depthOf(above) + 1);
    walking.delete(k);
    layer.set(k, d);
    return d;
  };
  for (const k of keys) depthOf(k);

  const rows: string[][] = [];
  for (const k of keys) (rows[layer.get(k)!] ??= []).push(k);
  for (let i = 0; i < rows.length; i++) rows[i] ??= [];

  // Orders each layer by where its neighbours are, down then up, a few times.
  const at = new Map<string, number>();
  const number = () => rows.forEach((row) => row.forEach((k, i) => at.set(k, i)));
  number();
  /** The average place of `k`'s neighbours (its own place when it has none). */
  const centre = (k: string, neighbours: string[]) => {
    const placed = neighbours.filter((n) => at.has(n));
    return placed.length ? placed.reduce((sum, n) => sum + at.get(n)!, 0) / placed.length : at.get(k)!;
  };
  for (let pass = 0; pass < 4; pass++) {
    const downwards = pass % 2 === 0;
    const order = downwards ? rows.map((_, i) => i) : rows.map((_, i) => rows.length - 1 - i);
    for (const i of order) {
      const row = rows[i]!;
      const score = new Map(row.map((k) => [k, centre(k, downwards ? up.get(k)! : down.get(k)!)]));
      row.sort((a, b) => score.get(a)! - score.get(b)! || at.get(a)! - at.get(b)!);
      row.forEach((k, j) => at.set(k, j));
    }
  }

  // Wraps layers wider than a tidy picture would be.
  const size = new Map(items.map((i) => [keyOf(i.item), i]));
  const area = items.reduce((sum, i) => sum + (i.w + COLUMN_GAP) * (i.h + ROW_GAP), 0);
  const widest = Math.max(...items.map((i) => i.w));
  const limit = Math.max(widest, 640, Math.sqrt(area) * 1.6);
  const lines: string[][] = [];
  for (const row of rows) {
    let line: string[] = [];
    let width = 0;
    for (const k of row) {
      const w = size.get(k)!.w;
      if (line.length && width + COLUMN_GAP + w > limit) {
        lines.push(line);
        line = [];
        width = 0;
      }
      width += (line.length ? COLUMN_GAP : 0) + w;
      line.push(k);
    }
    if (line.length) lines.push(line);
  }

  const lineWidth = (line: string[]) => line.reduce((sum, k) => sum + size.get(k)!.w, 0) + COLUMN_GAP * (line.length - 1);
  const w = Math.max(...lines.map(lineWidth));
  const inside: Placed[] = [];
  let y = 0;
  for (const line of lines) {
    let x = (w - lineWidth(line)) / 2;
    const h = Math.max(...line.map((k) => size.get(k)!.h));
    for (const k of line) {
      const s = size.get(k)!;
      inside.push({ item: s.item, x, y, w: s.w, h: s.h });
      x += s.w + COLUMN_GAP;
    }
    y += h + ROW_GAP;
  }
  return { w, h: y - ROW_GAP, inside };
}

/** Whether two boxes overlap, keeping a gap of `gap` around them. */
const overlaps = (a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }, gap: number) =>
  a.x < b.x + b.w + gap && b.x < a.x + a.w + gap && a.y < b.y + b.h + gap && b.y < a.y + a.h + gap;

/**
 * Puts the boxes where they were arranged; the others (nobody placed them yet) go in the first
 * free spot below a placed box they are linked to, else below everything, left to right.
 */
function arrange(boxes: DiagramBox[], links: Link[], arranged: BoxPositions): void {
  const placed: DiagramBox[] = [];
  const waiting: DiagramBox[] = [];
  for (const box of boxes) {
    const at = arranged[box.id];
    if (at && Number.isFinite(at[0]) && Number.isFinite(at[1])) {
      box.x = at[0];
      box.y = at[1];
      placed.push(box);
    } else waiting.push(box);
  }
  const byId = new Map(placed.map((b) => [b.id, b]));
  for (const box of waiting) {
    const link = links.find((l) => (l.from === box.id && byId.has(l.to)) || (l.to === box.id && byId.has(l.from)));
    const near = link ? byId.get(link.from === box.id ? link.to : link.from)! : null;
    const bottom = placed.length ? Math.max(...placed.map((b) => b.y + b.h)) : 0;
    const left = placed.length ? Math.min(...placed.map((b) => b.x)) : 0;
    // Users go below what they use; what is used goes above its users.
    const below = !near || link!.from === box.id;
    const start = near ? { x: near.x, y: below ? near.y + near.h + ROW_GAP : near.y - box.h - ROW_GAP } : { x: left, y: bottom + ROW_GAP };
    // Tries along the row (straight there, then right, left, further right...), then the next row.
    let spot = { ...start, w: box.w, h: box.h };
    for (let step = 1; step < 400 && placed.some((p) => overlaps(p, spot, COLUMN_GAP / 2)); step++) {
      const across = step % 9;
      const columns = across % 2 ? (across + 1) / 2 : -across / 2;
      const rows = Math.floor(step / 9);
      spot = { ...spot, x: start.x + columns * (box.w + COLUMN_GAP), y: start.y + rows * (box.h + ROW_GAP) * (below ? 1 : -1) };
    }
    box.x = spot.x;
    box.y = spot.y;
    placed.push(box);
    byId.set(box.id, box);
  }
}

/** The packages around their boxes and sub-packages, deepest first measured, shallow first listed. */
function wrapPackages(
  graph: Graph,
  used: Set<string>,
  parentOf: (id: string) => string | null,
  groupOfNode: (n: Node) => string | null,
  byId: Map<string, DiagramBox>,
): DiagramPackage[] {
  const depthOf = (id: string) => {
    let d = 0;
    for (let g = parentOf(id); g && d < 1000; g = parentOf(g)) d++;
    return d;
  };
  const groups = graph.groups.filter((g) => used.has(g.id)).map((g) => ({ g, depth: depthOf(g.id) }));
  const made = new Map<string, DiagramPackage>();
  for (const { g, depth } of [...groups].sort((a, b) => b.depth - a.depth)) {
    const rects: { x: number; y: number; w: number; h: number }[] = [];
    for (const box of byId.values()) if (groupOfNode(box.node) === g.id) rects.push(box);
    for (const [id, p] of made) if (parentOf(id) === g.id) rects.push(p);
    if (!rects.length) continue;
    const x1 = Math.min(...rects.map((r) => r.x)) - PACKAGE_PAD;
    const y1 = Math.min(...rects.map((r) => r.y)) - PACKAGE_PAD - PACKAGE_TAB;
    const x2 = Math.max(...rects.map((r) => r.x + r.w)) + PACKAGE_PAD;
    const y2 = Math.max(...rects.map((r) => r.y + r.h)) + PACKAGE_PAD;
    made.set(g.id, { id: g.id, name: g.name, x: x1, y: y1, w: x2 - x1, h: y2 - y1, depth, parent: parentOf(g.id) });
  }
  return [...made.values()].sort((a, b) => a.depth - b.depth);
}

/** The arrows: straight, border to border; links between the same two boxes side by side. */
function route(links: Link[], byId: Map<string, { x: number; y: number; w: number; h: number }>): DiagramEdge[] {
  const pairs = new Map<string, Link[]>();
  for (const l of links) {
    const key = l.from < l.to ? `${l.from}\n${l.to}` : `${l.to}\n${l.from}`;
    (pairs.get(key) ?? pairs.set(key, []).get(key)!).push(l);
  }
  const edges: DiagramEdge[] = [];
  for (const group of pairs.values()) {
    group.forEach((link, i) => {
      const a = byId.get(link.from)!;
      const b = byId.get(link.to)!;
      // Side by side: shifted across the line between the two centres.
      const shift = (i - (group.length - 1) / 2) * 12;
      const ax = a.x + a.w / 2;
      const ay = a.y + a.h / 2;
      const bx = b.x + b.w / 2;
      const by = b.y + b.h / 2;
      const len = Math.hypot(bx - ax, by - ay) || 1;
      // The same direction for both links of a pair, whichever way each one points.
      const sign = link.from < link.to ? 1 : -1;
      const nx = (-(by - ay) / len) * shift * sign;
      const ny = ((bx - ax) / len) * shift * sign;
      const [x1, y1] = border(a, bx + nx, by + ny);
      const [x2, y2] = border(b, ax + nx, ay + ny);
      edges.push({ link, x1: x1 + nx, y1: y1 + ny, x2: x2 + nx, y2: y2 + ny });
    });
  }
  return edges;
}

/** What the boxes and packages cover. */
function boundsOf(boxes: DiagramBox[], packages: DiagramPackage[]): DiagramLayout["bounds"] {
  const all = [...boxes, ...packages];
  if (!all.length) return null;
  const x = Math.min(...all.map((r) => r.x));
  const y = Math.min(...all.map((r) => r.y));
  return { x, y, w: Math.max(...all.map((r) => r.x + r.w)) - x, h: Math.max(...all.map((r) => r.y + r.h)) - y };
}
